import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl, FormBuilder, ReactiveFormsModule,
  ValidationErrors, Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { JjamesButtonComponent } from 'shared-lib';
import { MyLicensePlatesService } from '../my-license-plates.service';
import { LicensePlateType } from '../../../api/gen/model/licensePlateType';
import { PlateComponent, MAX_PLATE_LENGTH, PLATE_COUNTRIES, extractRegion, normalizePlate, sanitizePlateInput } from 'shared-lib';
/**
 * Registers a license plate onto the authenticated parker's own account
 * (POST /parker/me/license-plates). On success, returns to the list.
 */
@Component({
  selector: 'app-register-license-plate',
  imports: [CommonModule, ReactiveFormsModule, JjamesButtonComponent, PlateComponent],
  templateUrl: './register-license-plate.component.html',
  styleUrl: './register-license-plate.component.scss'
})
export class RegisterLicensePlateComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly api = inject(MyLicensePlatesService);

  protected readonly countries = PLATE_COUNTRIES;

  protected readonly plateTypes: { value: LicensePlateType; label: string }[] = [
    { value: 'Personal', label: 'Personal' },
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Government', label: 'Government' },
    { value: 'Other', label: 'Other' }
  ];

  protected readonly plateValue = signal('');
  protected readonly submitting = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly selectedCountryCode = signal('AT');
  /** District code parsed from the live input, for the preview's `L ⬥ 1234PP` split. */
  protected readonly plateRegion = signal('');

  protected readonly registerForm = this.fb.group({
    country: ['AT', Validators.required],
    // Pattern kept in step with sanitizePlateInput: it accepts exactly what the input filter lets
    // through, so a filtered value can never be stuck invalid.
    plate: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(12),
    Validators.pattern(/^[A-Z0-9ÄÖÜ]+$/)]],
    plateRepeat: ['', Validators.required],
    type: ['Personal' as LicensePlateType, Validators.required]
  }, { validators: [platesMatchValidator] });

  protected readonly selectedCountry = computed(() =>
    this.countries.find(c => c.code === this.selectedCountryCode()) ?? this.countries[0]);

  constructor() {
    this.registerForm.get('plate')!.valueChanges.subscribe(value => {
      this.plateValue.set(normalizePlate(value));
      this.plateRegion.set(extractRegion(value ?? '', this.selectedCountry().regionLength, this.selectedCountry().regions));
    });
    this.registerForm.get('country')!.valueChanges.subscribe(value => {
      this.selectedCountryCode.set(value ?? 'AT');
    });
  }

  protected readonly maxPlateLength = MAX_PLATE_LENGTH;

  /**
   * Rejects illegal characters (`;`, `[`, `]`, quotes, emoji …) as they are typed or pasted.
   * The caret is restored by hand so a stray character typed mid-plate does not throw the user
   * to the end of the field. Mirrors the JKU webshop field.
   */
  protected onPlateFieldInput(event: Event, control: 'plate' | 'plateRepeat'): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    const cleaned = sanitizePlateInput(raw);
    if (cleaned === raw) {
      return;
    }

    const caret = input.selectionStart ?? raw.length;
    const rejectedBeforeCaret = caret - sanitizePlateInput(raw.slice(0, caret)).length;
    this.registerForm.get(control)!.setValue(cleaned);
    input.value = cleaned;
    const restored = Math.max(0, caret - rejectedBeforeCaret);
    input.setSelectionRange(restored, restored);
  }

  protected get platesMismatch(): boolean {
    const repeatCtrl = this.registerForm.get('plateRepeat');
    return !!repeatCtrl?.touched
      && !!repeatCtrl.value
      && this.registerForm.hasError('platesMismatch');
  }

  protected onBack(): void {
    this.router.navigate(['/license-plates']);
  }

  protected onSubmit(): void {
    if (this.submitting()) {
      return;
    }
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.error.set(null);

    const raw = this.registerForm.getRawValue();
    this.api.add({
      fullNumber: normalizePlate(raw.plate),
      countryCode: raw.country ?? undefined,
      type: raw.type ?? 'Personal'
    }).subscribe({
      next: () => {
        this.submitting.set(false);
        this.router.navigate(['/license-plates']);
      },
      error: err => {
        this.submitting.set(false);
        // A 400 here is a business rule the parker can act on — the plate already belongs to
        // another account, or the site's plate limit is reached. The backend sends that reason as
        // plain text (`Title.Detail`), so show it instead of hiding it behind "check the details".
        const reason = err?.status === 400 && typeof err.error === 'string' ? err.error.trim() : '';
        this.error.set(reason || 'Could not register the license plate. Please check the details and try again.');
      }
    });
  }
}

function platesMatchValidator(group: AbstractControl): ValidationErrors | null {
  const plate = normalizePlate(group.get('plate')?.value);
  const repeat = normalizePlate(group.get('plateRepeat')?.value);
  return plate && repeat && plate !== repeat ? { platesMismatch: true } : null;
}
